import { defaultsDeep, isError } from 'lodash-es';
import { Sentry } from '@shared/sentry';
//TODO: refactor for ts

// extra parens required to allow JSDoc infer type "as const"
// eslint-disable-next-line no-extra-parens
const ERR = /** @type {const} */ {
  PFM_INIT: 'ERR_PFM_INIT',
  INIT_CONSUMER_TOKEN: 'ERR_INIT_CONSUMER_TOKEN',
  CREATE_BILL: 'ERR_CREATE_BILL',
  SEARCH_BILLS: 'ERR_SEARCH_BILLS',
  FETCH_BILL: 'ERR_FETCH_BILL',
  GET_PAYMENT_METHODS: 'ERR_GET_PAYMENT_METHODS',
  BILL_EXPIRED: 'ERR_BILL_EXPIRED',
  INIT_PAYMENT: 'ERR_INIT_PAYMENT',
  GET_ORDER: 'ERR_GET_ORDER',
  GET_BILL_OPTIONS: 'ERR_GET_BILL_OPTIONS',
  INVALID_ORDER_STATUS: 'ERR_INVALID_ORDER_STATUS',
  START_PAYMENT: 'ERR_START_PAYMENT',
  LOAD_QR: 'ERR_LOAD_QR',
  PROCESS_PAYMENT_METHOD: 'ERR_PROCESS_PAYMENT_METHOD',
  OPEN_ACQUIRING_LINK: 'ERR_OPEN_ACQUIRING_LINK',
  HANDLE_CASHBACK_SERVICE: 'ERR_HANDLE_CASHBACK_SERVICE',
  INIT_BOOKING_PAGE: 'ERR_INIT_BOOKING_PAGE',
  CHANGE_FARE: 'ERR_CHANGE_FARE',
  GET_OFFER_DETAILS: 'ERR_GET_OFFER_DETAILS',
  CREATE_ORDER: 'ERR_CREATE_ORDER',
  SUBMIT_BOOKING: 'ERR_SUBMIT_BOOKING',
  DOC_NUMBER_REGEX_TRUNCATE: 'ERR_DOC_NUMBER_REGEX_TRUNCATE',
  FETCH_PRODUCTS: 'ERR_FETCH_PRODUCTS',
  INIT_REMOTE_CONFIG: 'ERR_INIT_REMOTE_CONFIG',
  FETCH_REMOTE_CONFIG: 'ERR_FETCH_REMOTE_CONFIG',
  ACTIVATE_REMOTE_CONFIG: 'ERR_ACTIVATE_REMOTE_CONFIG',
  SERIALIZE_REMOTE_CONFIG: 'ERR_SERIALIZE_REMOTE_CONFIG',
  FETCH_LOYALTY_CUSTOMER: 'ERR_FETCH_LOYALTY_CUSTOMER',
  APPLY_CASHBACK: 'ERR_APPLY_CASHBACK',
  CANCEL_CASHBACK: 'ERR_CANCEL_CASHBACK',
  LOYALTY_BILL_CALCULATIONS: 'ERR_LOYALTY_BILL_CALCULATIONS',
  SET_USER_LOYALTY_PROPERTIES: 'ERR_SET_USER_LOYALTY_PROPERTIES',
  NATIVE_API_UNSUPPORTED_VERSION: 'ERR_UNSUPPORTED_NATIVE_API_VERSION',
  NATIVE_API_UNIMPLEMENTED_METHOD: 'ERR_UNIMPLEMENTED_NATIVE_API_METHOD',
};

/**
 * @description
 * This error can be thrown to
 * then finally catch it and pass to logError
 */
class ApplicationError extends Error {
  // TODO: Learn about this class and use for all errors if needed
  scopeContext: any;

  /**
   * @param {string} message
   * @param {{
   *   scopeContext?: import('@sentry/types').ScopeContext
   *   cause?: Error
   * }} params
   */
  constructor(message: string, { scopeContext, cause }: any = {}) {
    super(message);

    this.scopeContext = defaultsDeep(scopeContext, cause?.scopeContext) ?? null;
    this.cause = cause ?? null;
  }
}

function concatMessage(code: any, cause: Error) {
  const unPrefixedCode = cause.message.replace(/^ERR_/, '');
  const isPreDefinedCode = unPrefixedCode in ERR;

  return isPreDefinedCode
    ? `${code} > ${cause.message}`
    : `${code}: ${cause.message}`;
}

/**
 * @typedef LogParams
 * @property {Error} cause
 * @property {import('@sentry/types').ScopeContext} [scopeContext]
 *
 * @typedef {typeof ERR[keyof typeof ERR]} ErrorCode
 */

function normalizeArgs(
  errorOrCode: ApplicationError | typeof ERR[keyof typeof ERR] | Error,
  params: any,
) {
  if (errorOrCode instanceof ApplicationError) {
    return {
      code: errorOrCode.message,
      cause: errorOrCode.cause,
      scopeContext: defaultsDeep(params.scopeContext, errorOrCode.scopeContext),
    };
  }

  return {
    code: errorOrCode,
    scopeContext: params.scopeContext,
    cause: params.cause,
  };
}

/**
 * @param {ErrorCode | ApplicationError} errorOrCode
 * @param {LogParams} params
 */
function logError(
  errorOrCode: typeof ERR[keyof typeof ERR] | ApplicationError | Error,
  params = {},
) {
  const { code, scopeContext, cause } = normalizeArgs(errorOrCode, params);

  if (!isError(cause)) {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const error = new Error(code);
    Sentry.captureException(error, scopeContext);
    return;
  }

  const message = concatMessage(code, cause);
  const exception = new Error(message);
  exception.cause = cause;

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const finalScopeContext = defaultsDeep(scopeContext, cause.scopeContext);

  Sentry.captureException(exception, finalScopeContext);
}

export { ERR, ApplicationError, logError };
